import { useI18n } from "../../../../../../../application/language/useI18n";
import { inject } from "../../../../../../../common/container/inject";
import { ILearningRootService, LearningRootServiceToken } from "../../../../../service/LearningRootService";
import { ContentImportUI } from "./ContentImportUI";
import { IContentImportDomain } from "./IContentImportDomain";
import { IFileForImport } from "./IContentImportUI";



export class ContentImportDomain implements IContentImportDomain {
  private i18n = useI18n();

  constructor(
    public ui = new ContentImportUI(),
    private learningRootService = inject<ILearningRootService>(LearningRootServiceToken),
  ) {}

  handleFilesDrop = (files: FileList | File[]) => {
    this.ui.dropzoneErrors.setList([]);
    const current = this.ui.unitsForImport.list;
    const toAdd: IFileForImport[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      const exists = current.some((f) => f.file.name === file.name && f.file.size === file.size);
      if (exists) {
        errors.push(
          this.i18n.translate('contentTransfer.importPage.fileExist', { name: file.name }),
        );
      } else if (!file.name.toLowerCase().endsWith('.zip')) {
        errors.push(
          this.i18n.translate('contentTransfer.importPage.fileNotZip', { name: file.name }),
        );
      } else {
        toAdd.push({ file, status: 'pending' });
      }
    });

    if (toAdd.length > 0) {
      this.ui.unitsForImport.setList([...current, ...toAdd]);
    }
    if (errors.length > 0) {
      this.ui.dropzoneErrors.setList(errors);
      setTimeout(() => {
        this.ui.dropzoneErrors.setList([]);
      }, 5000);
    }
  };

  removeFile = (fileName: string) => {
    const updated = this.ui.unitsForImport.list.filter((f) => f.file.name !== fileName);
    this.ui.unitsForImport.setList(updated);
  };

  clearAllFiles = () => {
    this.ui.unitsForImport.setList([]);
  };

  openPasswordDialog = () => {
    this.ui.importPassword.setEntity('');
    this.ui.passwordError.setEntity(null);
    this.ui.isPasswordDialogOpen.setEntity(true);
  };

  closePasswordDialog = () => {
    this.ui.isPasswordDialogOpen.setEntity(false);
  };

  checkPasswordAndImport = async () => {
    this.closePasswordDialog();
    await this.importAllFiles();
  };

  importAllFiles = async () => {
    const files = [...this.ui.unitsForImport.list];
    if (files.length === 0) return;

    this.ui.isImporting.setEntity(true);
    this.ui.importProgress.setEntity({ current: 0, total: files.length });

    try {
      const results = await this.learningRootService.contentTransfer.importBatch(
        files.map((f) => f.file),
        this.ui.importPassword.entity,
        this.ui.withDependencies.entity,
      );

      this.ui.unitsForImport.setList(
        files.map((file, idx) => {
          const result = results[idx];
          return {
            ...file,
            status: result.success ? 'success' : 'error',
            message: result.success
              ? this.i18n.translate('contentTransfer.importPage.unitState')
              : result.message || this.i18n.translate('contentTransfer.importPage.importErrorDefault'),
          };
        }),
      );
    } catch (err: any) {
      this.ui.unitsForImport.setList(
        files.map((file) => {
          let errorMessage = this.i18n.translate('contentTransfer.importPage.importErrorDefault');

          if (err?.messageKey) {
            errorMessage = this.i18n.translate(err.messageKey, { message: err.message });
          } else if (err?.errorName) {
            errorMessage = this.i18n.translate(`WEB_MESSAGE.${err.errorName}` as any);
          } else if (err?.message) {
            errorMessage = err.message;
          }

          return {
            ...file,
            status: 'error',
            message: errorMessage,
          };
        }),
      );
    } finally {
      this.ui.importProgress.setEntity({ current: files.length, total: files.length });
      this.ui.isImporting.setEntity(false);
    }
  };
  togglePasswordVisibility = () => {
    this.ui.isPasswordVisible.setEntity(!this.ui.isPasswordVisible.entity)
  }
}
